import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import AccountLayout from '@/components/feature/AccountLayout';
import ConfirmModal from '@/components/common/ConfirmModal';
import { useAuth } from '@/hooks/useAuth';
import {
  fetchProviderJobResumeDraft,
  updateProviderJobResumeDraft,
  fetchMyJobApplications,
  fetchMyJobApplicationDetail,
  withdrawJobApplication,
} from '@/service/api/resume';
import {
  JobResumeDraftModule,
  JobApplicationListModule,
  JobApplicationDetailModal,
} from './modules';

export default function MyJobResumePage() {
  const navigate = useNavigate();
  const { user, isEnterprise } = useAuth();

  const [searchParams, setSearchParams] = useSearchParams();
  // 默认情况下优先展示申请列表 ('applications')
  const initialTab = searchParams.get('tab') === 'draft' ? 'draft' : 'applications';
  const [activeTab, setActiveTab] = useState<'draft' | 'applications'>(initialTab);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'applications' || tabParam === 'draft') {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const handleTabChange = (tab: 'draft' | 'applications') => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // 草稿表单状态
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [expectedMonthlySalary, setExpectedMonthlySalary] = useState('');
  const [resumeAssetId, setResumeAssetId] = useState('');
  const [resumeFileObj, setResumeFileObj] = useState<Api.Resume.ResumePortfolioAssetResponse | null>(null);

  // 投递申请列表状态
  const [applications, setApplications] = useState<Api.Resume.ApplicationResponse[]>([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [appsPage, setAppsPage] = useState(1);
  const [appsTotal, setAppsTotal] = useState(0);
  const [appsPageSize] = useState(10);
  const [confirmWithdrawId, setConfirmWithdrawId] = useState<string | null>(null);
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  // 申请详情 Modal 状态
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedAppDetail, setSelectedAppDetail] = useState<Api.Resume.ApplicationResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // 防止重复初始化的 ref 标记
  const draftFetchedRef = useRef(false);
  const lastAppsFetchKeyRef = useRef<string | null>(null);

  // 加载求职草稿数据并检测是否已填简历 (只触发一次)
  useEffect(() => {
    if (isEnterprise || draftFetchedRef.current) {
      if (isEnterprise) setLoading(false);
      return;
    }
    draftFetchedRef.current = true;

    setLoading(true);
    setErrorMsg('');
    fetchProviderJobResumeDraft()
      .then((res) => {
        let hasFilled = false;
        if (res) {
          setExpectedMonthlySalary(res.expected_monthly_salary || '');
          if (res.resume_file) {
            setResumeFileObj(res.resume_file);
            setResumeAssetId(res.resume_file.id || '');
          }
          const hasSalary = Boolean(res.expected_monthly_salary && res.expected_monthly_salary.trim());
          const hasFile = Boolean(res.resume_file?.id || res.resume_file?.access_url);
          hasFilled = hasSalary || hasFile;
        }

        const currentTabParam = searchParams.get('tab');
        // 未在 URL 中显式指定 tab 且未填写简历时，自动引导至填写简历草稿页
        if (!currentTabParam && !hasFilled) {
          setActiveTab('draft');
          setErrorMsg('提示：您尚未填写求职期望或上传简历，请先完善求职资料后再投递岗位。');
        }
      })
      .catch((err) => {
        console.error('获取求职资料草稿异常:', err);
        setErrorMsg(err instanceof Error ? err.message : '获取求职资料失败');
        draftFetchedRef.current = false;
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isEnterprise, searchParams]);

  // 加载我的求职申请列表 (缓存 key 防重)
  const loadMyApplications = useCallback(async (page: number, force = false) => {
    if (isEnterprise) return;
    const fetchKey = `${page}_${appsPageSize}`;
    if (!force && lastAppsFetchKeyRef.current === fetchKey) return;
    lastAppsFetchKeyRef.current = fetchKey;

    setAppsLoading(true);
    try {
      const res = await fetchMyJobApplications({ page, page_size: appsPageSize });
      setApplications(res?.items || []);
      setAppsTotal(res?.total || 0);
      setAppsPage(page);
    } catch (err) {
      console.error('获取求职申请列表失败:', err);
      lastAppsFetchKeyRef.current = null;
    } finally {
      setAppsLoading(false);
    }
  }, [isEnterprise, appsPageSize]);

  useEffect(() => {
    if (activeTab === 'applications') {
      loadMyApplications(1);
    }
  }, [activeTab, loadMyApplications]);

  // 提交/保存求职草稿
  const handleSubmitDraft = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!expectedMonthlySalary.trim() && !resumeAssetId) {
      setErrorMsg('请填写求职期望月薪并上传简历文件');
      return;
    }
    if (!expectedMonthlySalary.trim()) {
      setErrorMsg('请填写求职期望月薪');
      return;
    }
    if (!resumeAssetId) {
      setErrorMsg('请上传简历文件');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const res = await updateProviderJobResumeDraft({
        expected_monthly_salary: expectedMonthlySalary,
        resume_file_upload_asset_id: resumeAssetId,
      });

      if (res) {
        setExpectedMonthlySalary(res.expected_monthly_salary || expectedMonthlySalary);
        if (res.resume_file) {
          setResumeFileObj(res.resume_file);
          setResumeAssetId(res.resume_file.id || resumeAssetId);
        }
      }

      setSuccessMsg('求职资料草稿保存成功！现在您可以投递招聘岗位');
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      console.error('保存求职资料草稿失败:', err);
      setErrorMsg(err instanceof Error ? err.message : '保存失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  // 查看职位申请详情 (跳转至关联的招聘岗位详情页)
  const handleViewDetail = (jobPostingId?: string) => {
    if (!jobPostingId) return;
    navigate(`/jobs/${jobPostingId}`);
  };

  // 确认撤回求职申请
  const handleConfirmWithdraw = async () => {
    if (!confirmWithdrawId) return;
    setIsWithdrawing(true);
    try {
      await withdrawJobApplication(confirmWithdrawId);
      setSuccessMsg('求职申请已成功撤回');
      setTimeout(() => setSuccessMsg(''), 3000);
      loadMyApplications(appsPage, true);
    } catch (err: any) {
      setErrorMsg(err?.message || '撤回申请失败');
    } finally {
      setIsWithdrawing(false);
      setConfirmWithdrawId(null);
    }
  };

  if (isEnterprise) {
    return (
      <AccountLayout
        activePath="/my-job-resume"
        breadcrumbs={[{ label: '账号中心', href: '/profile' }, { label: '我的求职' }]}
      >
        <div className="glass-card rounded-2xl p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-4 text-2xl">
            <i className="ri-building-line"></i>
          </div>
          <h2 className="font-heading font-bold text-lg text-foreground-950 mb-2">企业账号提示</h2>
          <p className="text-sm text-foreground-500 max-w-md mx-auto">
            【我的求职】功能仅面向个人账号开放。企业账号可以前往【招聘管理】发布或管理招聘岗位。
          </p>
        </div>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout
      activePath="/my-job-resume"
      breadcrumbs={[{ label: '账号中心', href: '/profile' }, { label: '我的求职' }]}
    >
      <div className="max-w-4xl space-y-6">
        {/* 顶部 Tab 导航卡片 */}
        <div className="glass-card rounded-2xl p-6 md:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-5 mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center text-xl">
                <i className="ri-briefcase-line"></i>
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl text-foreground-950">我的求职管理</h1>
                <p className="text-xs text-foreground-500 mt-0.5">
                  维护个人求职期望与简历附件，管理投递岗位申请记录
                </p>
              </div>
            </div>

            {/* 选项卡按钮 */}
            <div className="flex rounded-xl bg-background-100 p-1 border border-background-200/80 shrink-0 self-start sm:self-auto">
              <button
                type="button"
                onClick={() => handleTabChange('applications')}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                  activeTab === 'applications'
                    ? 'bg-white text-primary-600 shadow-xs font-bold'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className="ri-send-plane-line mr-1.5"></i>
                我的投递记录
                {appsTotal > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-primary-100 text-primary-700">
                    {appsTotal}
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={() => handleTabChange('draft')}
                className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                  activeTab === 'draft'
                    ? 'bg-white text-primary-600 shadow-xs font-bold'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className="ri-file-text-line mr-1.5"></i>
                求职资料草稿
              </button>
            </div>
          </div>

          {/* 全局消息提示 */}
          {successMsg && (
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200/60 text-emerald-700 text-sm flex items-center gap-2">
              <i className="ri-checkbox-circle-fill text-lg text-emerald-500"></i>
              {successMsg}
            </div>
          )}

          {errorMsg && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200/60 text-red-600 text-sm flex items-center gap-2">
              <i className="ri-error-warning-fill text-lg text-red-500"></i>
              {errorMsg}
            </div>
          )}
        </div>

        {/* TAB 1: 求职资料草稿模块 */}
        {activeTab === 'draft' && (
          <JobResumeDraftModule
            loading={loading}
            submitting={submitting}
            expectedMonthlySalary={expectedMonthlySalary}
            setExpectedMonthlySalary={setExpectedMonthlySalary}
            resumeAssetId={resumeAssetId}
            setResumeAssetId={setResumeAssetId}
            resumeFileObj={resumeFileObj}
            setResumeFileObj={setResumeFileObj}
            handleSubmitDraft={handleSubmitDraft}
          />
        )}

        {/* TAB 2: 我的投递记录列表模块 */}
        {activeTab === 'applications' && (
          <JobApplicationListModule
            applications={applications}
            appsLoading={appsLoading}
            appsPage={appsPage}
            appsTotal={appsTotal}
            appsPageSize={appsPageSize}
            onLoadPage={(page) => loadMyApplications(page)}
            onViewDetail={(id) => handleViewDetail(id)}
            onWithdraw={(id) => setConfirmWithdrawId(id || null)}
          />
        )}
      </div>

      {/* 申请详情 Modal */}
      <JobApplicationDetailModal
        open={detailModalOpen}
        loading={detailLoading}
        detail={selectedAppDetail}
        onClose={() => setDetailModalOpen(false)}
      />

      {/* 撤回申请 ConfirmModal */}
      <ConfirmModal
        open={Boolean(confirmWithdrawId)}
        title="确认撤回求职申请？"
        content="撤回后该岗位的申请记录将更新为已撤回状态，如需再次投递可在岗位页面重新提交。"
        confirmText="确认撤回"
        cancelText="取消"
        type="danger"
        loading={isWithdrawing}
        onConfirm={handleConfirmWithdraw}
        onCancel={() => setConfirmWithdrawId(null)}
      />
    </AccountLayout>
  );
}
